// SPDX-License-Identifier: GPL-3.0-or-later
// The feedback that the table rules emit. The rules call small methods here at the places where a
// score or a state already changes. This class only builds an event and gives it to the sink.
// It reads the rules state and never writes it, so a rule outcome is the same with or without it.
//
// What is here: ball contacts (from each `PhysicsEvent`) and table moments that only the rules
// know (jackpot, combo, skill shot, lock, multiball, tilt, bank complete). Engine events (score,
// mode, ball save, game over) come from `engine-bridge.ts`. Flipper and plunger input come from
// `input-bridge.ts`.

import type { PhysicsEvent } from "../../shared/contracts.ts";
import {
  type FeedbackEvent,
  type FeedbackSink,
  strengthFromSpeed,
} from "../../shared/feedback/index.ts";
import type { ShotId } from "../callouts.ts";
import { DECORATION, ITEM } from "../ids.ts";
import type { PositionOf } from "./positions.ts";

type ContactKind =
  | "bumper"
  | "sling"
  | "target"
  | "spinner"
  | "ramp"
  | "orbit"
  | "lane"
  | "kicker"
  | "plunger_launch";

/** What a ball contact with one item is. The event type must match, as in the rules routes. */
interface Contact {
  readonly kind: ContactKind;
  readonly type: PhysicsEvent["type"];
  readonly character?: string;
}

const CONTACTS: ReadonlyMap<string, Contact> = buildContacts();

function buildContacts(): ReadonlyMap<string, Contact> {
  const map = new Map<string, Contact>();
  const add = (items: readonly string[], contact: Contact): void => {
    for (const item of items) map.set(item, contact);
  };
  add([ITEM.bumper1, ITEM.bumper2, ITEM.bumper3], {
    kind: "bumper",
    type: "hit",
    character: DECORATION.randy,
  });
  add([ITEM.slingLeft, ITEM.slingRight], { kind: "sling", type: "slingshot" });
  add([ITEM.token1, ITEM.token2, ITEM.token3], {
    kind: "target",
    type: "hit",
    character: DECORATION.carl,
  });
  add([ITEM.denyD, ITEM.denyE, ITEM.denyN, ITEM.denyY], { kind: "target", type: "hit" });
  add([ITEM.spinner], { kind: "spinner", type: "spin" });
  add([ITEM.orbitLeftTop], { kind: "orbit", type: "hit" });
  add([ITEM.orbitRightTop], { kind: "orbit", type: "hit", character: DECORATION.tessa });
  add([ITEM.rampLeftExit], { kind: "ramp", type: "hit", character: DECORATION.haley });
  add([ITEM.rampRightExit], { kind: "ramp", type: "hit" });
  add([ITEM.laneL, ITEM.laneG, ITEM.laneT, ITEM.laneM, ITEM.topAccept, ITEM.topAll, ITEM.topYes], {
    kind: "lane",
    type: "hit",
    character: DECORATION.larry,
  });
  add([ITEM.saucer], { kind: "kicker", type: "hit" });
  add([ITEM.kickback], { kind: "kicker", type: "hit", character: DECORATION.dennis });
  add([ITEM.shooter], { kind: "plunger_launch", type: "unhit" });
  return map;
}

/** The table item of each orbit and ramp shot. */
const SHOT_ITEM: Readonly<Record<ShotId, string>> = {
  rampLeft: ITEM.rampLeftExit,
  rampRight: ITEM.rampRightExit,
  orbitLeft: ITEM.orbitLeftTop,
  orbitRight: ITEM.orbitRightTop,
};

/** The emitter of the table rules. */
export class RuleFeedback {
  constructor(
    private readonly sink: FeedbackSink,
    private readonly positionOf: PositionOf,
  ) {}

  /** Give an event to the sink. A sink that throws must not change a rule outcome. */
  private send(ev: FeedbackEvent): void {
    try {
      this.sink.emit(ev);
    } catch {
      // Feedback is observe-only. The game goes on.
    }
  }

  /** A ball contact. Only a ball (id 1 or more) makes one. A drain is a contact with the drain. */
  contact(ev: PhysicsEvent): void {
    if (ev.ballId <= 0) return;
    const strength = strengthFromSpeed(ev.speed);
    if (ev.type === "drain") {
      this.send({ kind: "drain", element: ev.item, pos: this.positionOf(ev.item), strength });
      return;
    }
    const contact = CONTACTS.get(ev.item);
    if (!contact || contact.type !== ev.type) return;
    this.send({
      kind: contact.kind,
      element: ev.item,
      pos: this.positionOf(ev.item),
      strength,
      characterId: contact.character,
    });
  }

  /** A jackpot of SigmaBot Swarm. `shot` is the shot, or null for the super jackpot at the saucer. */
  jackpot(shot: ShotId | null, points: number): void {
    const element = shot === null ? ITEM.saucer : SHOT_ITEM[shot];
    this.send({
      kind: "jackpot",
      superJackpot: shot === null,
      element,
      pos: this.positionOf(element),
      strength: 1,
      scoreDelta: points,
      label: shot === null ? "Super jackpot" : "Jackpot",
      characterId: DECORATION.rex,
    });
  }

  /** A combo of two shots. `label` is the score reason. */
  combo(label: string, points: number): void {
    this.send({ kind: "combo", strength: 1, scoreDelta: points, label });
  }

  /** The skill shot, or the super skill shot at the right ramp. */
  skillShot(element: string, label: string, points: number): void {
    this.send({
      kind: "skill_shot",
      element,
      pos: this.positionOf(element),
      strength: 1,
      scoreDelta: points,
      label,
    });
  }

  /** A drop target bank is complete (TOKENS, or the word DENY). */
  bankComplete(label: string, points: number, character?: string): void {
    this.send({
      kind: "drop_bank_complete",
      strength: 1,
      scoreDelta: points,
      label,
      characterId: character,
    });
  }

  /** A ball is locked at the saucer. `count` is the number of locked balls. */
  ballLock(count: number): void {
    this.send({
      kind: "ball_lock",
      count,
      element: ITEM.saucer,
      pos: this.positionOf(ITEM.saucer),
      strength: 1,
    });
  }

  /** Multiball starts. */
  multiballStart(balls: number): void {
    this.send({
      kind: "multiball_start",
      balls,
      strength: 1,
      label: "SigmaBot Swarm",
      characterId: DECORATION.rex,
    });
  }

  /** A tilt warning. `count` is 1 or 2. */
  tiltWarning(count: number): void {
    this.send({ kind: "tilt_warning", count, strength: Math.min(1, count / 3) });
  }

  /** The tilt: the ball ends. */
  tilt(): void {
    this.send({ kind: "tilt", strength: 1 });
  }
}
